import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./clientModal.css";
import { useIntl } from "react-intl";

import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";

import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `/js/pdf.worker.min.mjs`;

export default function ClientFileModalContent({
  selectedContent,
  onCloseClick,
  user,
}) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const objectUrlRef = useRef(null);

  const fetchFile = async () => {
    let objectUrl = null;

    if (
      selectedContent?.file &&
      selectedContent?.name &&
      selectedContent?.extension
    ) {
      try {
        setLoading(true);
        const response = await fetch(selectedContent.file, {
          method: "GET",
          headers: {
            "Content-Type": "application/octet-stream",
          },
        });

        if (!response.ok) {
          toast.error(intl.formatMessage({ id: "failed-to-load-the-file" }));
        }

        const result = await response.blob();
        objectUrl = window.URL.createObjectURL(result);
      } catch (e) {
        toast.error(e.message);
      }
    }

    setFileUrl(objectUrl);
    setLoading(false);

    return objectUrl;
  };

  useEffect(() => {
    const loadFile = async () => {
      const objectUrl = await fetchFile();
      objectUrlRef.current = objectUrl;
    };
    loadFile();
    return () => {
      if (objectUrlRef.current) {
        window.URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
        setFileUrl(null);
      }
    };
  }, []);

  const downloadFile = () => {
    if (!fileUrl) {
      return;
    }
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = `${selectedContent.name}${selectedContent.extension}`;
    a.click();
    a.remove();
  };

  return (
    <div className="c-modal-wrapper">
      <div className="c-modal-left">
        <div className="c-left-top">
          <div className="c-doc-viewer-shell">
            {fileUrl ? (
              <DocViewer
                prefetchMethod="GET"
                pluginRenderers={DocViewerRenderers}
                documents={[
                  { uri: selectedContent?.file, fileName: selectedContent?.name },
                ]}
              />
            ) : (
              <img
                src={selectedContent?.displayImage}
                alt={selectedContent?.name}
                className="c-modal-image"
              />
            )}
          </div>
        </div>

        <div className="pt-3 c-left-bottom">
          {selectedContent?.link ? (
            <a
              href={selectedContent.link}
              target="_blank"
              rel="noreferrer"
              className="mt-1 mt-md-2 content-button"
            >
              {intl.formatMessage({ id: "open-link" })}
            </a>
          ) : (
            <button
              type="button"
              disabled={loading || !fileUrl}
              className="mt-3 mt-md-2 content-button"
              onClick={() => downloadFile()}
            >
              {loading
                ? intl.formatMessage({ id: "loading" })
                : intl.formatMessage({ id: "download" })}
            </button>
          )}
        </div>
      </div>

      <div className="c-modal-right">
        <button
          className="c-modal-action"
          disabled={loading}
          onClick={onCloseClick}
        >
          <div className="c-modal-close">
            <i className="fas fa-times" />
          </div>
        </button>

        <div className="c-modal-right-content">
          <div className="c-modal-right-file">
            <div className="c-doc-viewer-shell">
              {fileUrl ? (
                <DocViewer
                  prefetchMethod="GET"
                  pluginRenderers={DocViewerRenderers}
                  documents={[
                    {
                      uri: selectedContent?.file,
                      fileName: selectedContent?.name,
                    },
                  ]}
                />
              ) : (
                <img
                  src={selectedContent?.displayImage}
                  alt={selectedContent?.name}
                  className="c-modal-image"
                />
              )}
            </div>

            {selectedContent?.link ? (
              <a
                href={selectedContent.link}
                target="_blank"
                rel="noreferrer"
                className="mt-1 mt-md-2 content-button"
              >
                {intl.formatMessage({ id: "open-link" })}
              </a>
            ) : (
              <button
                type="button"
                disabled={loading || !fileUrl}
                className="mt-4 mt-md-2 content-button"
                onClick={() => downloadFile()}
              >
                {loading
                  ? intl.formatMessage({ id: "loading" })
                  : intl.formatMessage({ id: "download" })}
              </button>
            )}
          </div>

          <div className="c-modal-content-head">
            <i className="fas fa-info-circle text-dark mr-2" />
            <span>{intl.formatMessage({ id: "details" })}</span>
          </div>

          <div className="c-modal-content-title mt-4">
            {intl.formatMessage({ id: "title" })}
          </div>
          <div className="c-modal-content-name">{selectedContent?.name}</div>

          <div className="c-modal-content-title mt-4">
            {intl.formatMessage({ id: "description" })}
          </div>
          <div className="mt-1">{selectedContent?.description}</div>

          <div className="c-modal-content-title mt-4">
            {intl.formatMessage({ id: "general-info" })}
          </div>
          <table className="mt-1 table table-sm table-borderless">
            {selectedContent?.link ? (
              <>
                <tr>
                  <td>{intl.formatMessage({ id: "link" })}</td>
                  <td>
                    <a
                      href={selectedContent.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {selectedContent?.link}
                    </a>
                  </td>
                </tr>
              </>
            ) : (
              <>
                <tr>
                  <td>{intl.formatMessage({ id: "type" })}</td>
                  <td>{selectedContent?.fileType}</td>
                </tr>
                <tr>
                  <td>{intl.formatMessage({ id: "size" })}</td>
                  <td>{selectedContent?.size}</td>
                </tr>
                <tr>
                  <td>{intl.formatMessage({ id: "uploaded-by" })}</td>
                  <td>{user?.name}</td>
                </tr>
              </>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
